// Where every setting sits on the settings screens (spec/13 §13, 2026-10-07): grouped by the job a café owner is doing,
// not by the code module that declares it. One place, presentation only — the registry's `group` stays what it was,
// and nothing here changes a key, a stored value, a default, a scope, a lock or a permission.
//
//   group      the job: Venue, Time & pricing, Sessions & PCs, Money, Cash & shifts, Food & orders, Players &
//              sign-in, Staff & limits, Hardware, Security, Notifications, Privacy & records
//   section    two to four sub-headings inside a group, important rows first
//   advanced   rarely changed: drawn inside the group's collapsed "Advanced" disclosure
//   dependsOn  a dependant nests under its master and collapses with "Turn on … to use these" while the master says
//              it does nothing — declared here, read by the renderer, tested (`settings-layout.test.ts`)
//   home       shown elsewhere: pay-as-you-go lives in Pricing → Pay as you go (a summary row links there); the
//              venue's tax mode and GSTIN are the tax registration panel's
//   under      drawn as a sub-option of another setting (`receipts.autoEmail` under `notifications.receiptDefault`)
//   display    how the value is shown and typed — seconds as minutes, basis points as a percentage — converted back
//              exactly on save (`units.ts`)
//   control    a purpose-made control: a role picker, a page picker, path presets, a time range…
//
// The completeness test loads every setting the server registers and fails when one is not placed here exactly once,
// so a new setting can never silently vanish or land unordered. A key this map does not know (a test's own setting) is
// still drawn — at the end of the group its registry group maps to (`FALLBACK_GROUP`).

import type { SettingGroup } from '@clavian/api-client';

export const JOB_GROUP_IDS = [
  'venue',
  'timeAndPricing',
  'sessionsAndPcs',
  'money',
  'cashAndShifts',
  'foodAndOrders',
  'playersAndSignIn',
  'staffLimits',
  'hardware',
  'security',
  'notifications',
  'privacyAndRecords',
  'clavianBoot',
] as const;
export type JobGroupId = (typeof JOB_GROUP_IDS)[number];

/** A panel with its own endpoints, drawn in a section beside (or instead of) registry rows. */
export type PanelId =
  | 'venueProfile'
  | 'payg'
  | 'businessDetails'
  | 'taxRegistration'
  | 'taxCategories'
  | 'counters'
  | 'printers'
  | 'daysAndShifts'
  | 'offlinePlay'
  | 'limits'
  | 'ageRules'
  | 'onlinePaymentsStatus';

/** When a master lets its dependants do something. */
export type Condition =
  | { readonly is: 'on' }
  | { readonly is: 'positive' }
  | { readonly is: 'equals'; readonly value: string | number | boolean }
  | { readonly is: 'not'; readonly value: string | number | boolean };

export interface Dependency {
  readonly key: string;
  readonly when: Condition;
}

/** How a number is shown and typed (`units.ts`); the stored value never changes. */
export type DisplayUnit =
  | { readonly kind: 'duration'; readonly stored: 'seconds' | 'minutes' | 'hours' | 'days' | 'months' | 'years'; readonly shown?: 'minutes' | 'hours' }
  | { readonly kind: 'percentBps' }
  | { readonly kind: 'count'; readonly per: 'week' | 'month' }
  | { readonly kind: 'celsius' };

/** A purpose-made control for a value the generic renderer would draw as a bare list or a text box. */
export type ControlHint =
  | 'minutesList'
  | 'spokenWarnings'
  | 'rolePicker'
  | 'settingsPages'
  | 'savePaths'
  | 'fileNames'
  | 'extensionIds'
  | 'updateWindow'
  | 'channelOrder'
  | 'version'
  | 'upiId'
  | 'prefix';

export interface Placement {
  readonly key: string;
  readonly advanced?: boolean;
  readonly home?: 'pricing' | 'taxRegistration';
  readonly dependsOn?: readonly Dependency[];
  /** A master whose value makes this one do nothing — strict offline lock over the other offline options. */
  readonly overriddenBy?: Dependency;
  readonly under?: string;
  readonly display?: DisplayUnit;
  readonly control?: ControlHint;
}

export interface LayoutSection {
  readonly id: string;
  readonly settings: readonly Placement[];
  /** A panel drawn at the head of the section. */
  readonly panel?: PanelId;
  /** The panel drawn at its head on the organisation's screen instead, where `panel` is a venue's (ADR-0046). */
  readonly organizationPanel?: PanelId;
  /** A panel drawn after the section's rows: the tax categories, under the tax settings. */
  readonly panelAfter?: PanelId;
}

export interface LayoutGroup {
  readonly id: JobGroupId;
  readonly sections: readonly LayoutSection[];
}

const on = (key: string): Dependency => ({ key, when: { is: 'on' } });
const minutes = { kind: 'duration', stored: 'minutes' } as const satisfies DisplayUnit;
const hours = { kind: 'duration', stored: 'hours' } as const satisfies DisplayUnit;
const days = { kind: 'duration', stored: 'days' } as const satisfies DisplayUnit;

export const LAYOUT: readonly LayoutGroup[] = [
  {
    id: 'venue',
    sections: [
      // The venue record — status, details, opening hours, holidays — and the closing-time rule, with one Save.
      { id: 'profile', panel: 'venueProfile', settings: [{ key: 'venue.closingAction' }] },
      // The organisation's colours on its venues' lock screens and receipts (organisation and HQ screens).
      { id: 'brand', settings: [{ key: 'org.branding' }] },
    ],
  },
  {
    id: 'timeAndPricing',
    sections: [
      // One home per thing: pay-as-you-go is edited in Pricing → Pay as you go; this section is its summary row.
      {
        id: 'payg',
        panel: 'payg',
        settings: [
          { key: 'sessions.payAsYouGoEnabled', home: 'pricing' },
          { key: 'sessions.paygHourlyRate', home: 'pricing', dependsOn: [on('sessions.payAsYouGoEnabled')] },
          { key: 'sessions.paygBillingUnitSeconds', home: 'pricing', dependsOn: [on('sessions.payAsYouGoEnabled')] },
          { key: 'sessions.paygDefaultBudget', home: 'pricing', dependsOn: [on('sessions.payAsYouGoEnabled')] },
          {
            key: 'sessions.walletFundsPlay',
            home: 'pricing',
            dependsOn: [on('wallet.enabled'), on('sessions.payAsYouGoEnabled'), { key: 'sessions.paygHourlyRate', when: { is: 'positive' } }],
          },
        ],
      },
      {
        id: 'grace',
        settings: [
          { key: 'sessions.graceSeconds', display: { kind: 'duration', stored: 'seconds', shown: 'minutes' } },
          { key: 'sessions.graceCloseGames', dependsOn: [{ key: 'sessions.graceSeconds', when: { is: 'positive' } }] },
          { key: 'sessions.graceOncePerSession', dependsOn: [{ key: 'sessions.graceSeconds', when: { is: 'positive' } }] },
        ],
      },
      {
        id: 'warnings',
        settings: [
          { key: 'sessions.warningMinutes', control: 'minutesList' },
          { key: 'sessions.voiceWarnings', control: 'spokenWarnings' },
          { key: 'sessions.staffAlertMinutes', control: 'minutesList', advanced: true },
        ],
      },
      { id: 'passes', settings: [{ key: 'passes.giftsPerMonth', display: { kind: 'count', per: 'month' } }] },
    ],
  },
  {
    id: 'sessionsAndPcs',
    sections: [
      {
        id: 'away',
        settings: [
          { key: 'sessions.lockPausesBilling' },
          { key: 'sessions.maxLockMinutes', display: minutes },
          { key: 'sessions.autoLockIdleMinutes', display: minutes, advanced: true },
        ],
      },
      {
        id: 'cleanup',
        settings: [
          { key: 'cleanup.profile' },
          { key: 'cleanup.gameSaveAllowList', control: 'savePaths', dependsOn: [{ key: 'cleanup.profile', when: { is: 'equals', value: 'full' } }] },
          { key: 'cleanup.keepLauncherAccounts', advanced: true },
          { key: 'cleanup.maxSeconds', advanced: true, display: { kind: 'duration', stored: 'seconds' } },
        ],
      },
      {
        id: 'offline',
        panel: 'offlinePlay',
        settings: [
          { key: 'offline.continueRunning', overriddenBy: on('offline.strictLock') },
          { key: 'offline.graceMinutes', display: minutes, dependsOn: [on('offline.continueRunning')] },
          { key: 'offline.staffCodes', overriddenBy: on('offline.strictLock') },
          { key: 'offline.knownPlayers', overriddenBy: on('offline.strictLock') },
          { key: 'offline.knownPlayerMaxMinutes', display: minutes, dependsOn: [on('offline.knownPlayers')] },
          { key: 'offline.knownPlayerWindowDays', display: days, dependsOn: [on('offline.knownPlayers')] },
          { key: 'offline.strictLock' },
        ],
      },
      {
        id: 'kioskScreen',
        settings: [{ key: 'kiosk.trailers' }, { key: 'kiosk.uiSounds' }],
      },
      {
        id: 'calls',
        settings: [
          { key: 'calls.reAlertMinutes', display: minutes, advanced: true },
          { key: 'calls.expireMinutes', display: minutes, advanced: true },
        ],
      },
    ],
  },
  {
    id: 'money',
    sections: [
      {
        id: 'payments',
        panel: 'onlinePaymentsStatus',
        settings: [
          { key: 'payments.onlineEnabled' },
          { key: 'payments.commissionBps', display: { kind: 'percentBps' } },
          { key: 'payments.counterUpiVpa', control: 'upiId' },
          { key: 'payments.intentTtlMinutes', display: minutes, advanced: true },
          { key: 'billing.franchiseeBilledCentrally', advanced: true },
        ],
      },
      {
        id: 'wallet',
        settings: [
          { key: 'wallet.enabled' },
          { key: 'wallet.topupPresets', dependsOn: [on('wallet.enabled')] },
          { key: 'wallet.topupOffers', dependsOn: [on('wallet.enabled')] },
          { key: 'wallet.minTopupAmount', dependsOn: [on('wallet.enabled')] },
          { key: 'wallet.maxTopupAmount', dependsOn: [on('wallet.enabled')] },
          { key: 'wallet.maxBalanceAmount', dependsOn: [on('wallet.enabled')], advanced: true },
          { key: 'wallet.spendOrder', dependsOn: [on('wallet.enabled')], advanced: true },
          // Pay later sits with the wallet: the two other ways a player pays without paying now.
          { key: 'credit.enabled' },
          { key: 'credit.limitAmount', dependsOn: [on('credit.enabled')] },
        ],
      },
      {
        id: 'refunds',
        settings: [
          { key: 'passes.fullRefundWithinHours', display: hours },
          { key: 'passes.fullRefundUsedSeconds', display: { kind: 'duration', stored: 'seconds', shown: 'minutes' }, advanced: true },
          { key: 'passes.proRataRefunds' },
          { key: 'wallet.cashRefundsAllowed', dependsOn: [on('wallet.enabled')] },
        ],
      },
      {
        id: 'tax',
        // A venue's tax registration on a venue's screen; on the organisation's, its business details — the legal name,
        // PAN and registered address every GST document is issued under (ADR-0046).
        panel: 'taxRegistration',
        organizationPanel: 'businessDetails',
        panelAfter: 'taxCategories',
        settings: [
          { key: 'tax.mode', home: 'taxRegistration' },
          { key: 'tax.gstin', home: 'taxRegistration' },
          { key: 'tax.pricesInclusive' },
          { key: 'tax.playTaxed' },
          { key: 'tax.playTaxCategory', dependsOn: [on('tax.playTaxed')] },
          { key: 'tax.customLineCategory', advanced: true },
          { key: 'tax.walletTopupTaxPoint', dependsOn: [on('wallet.enabled')], advanced: true },
          { key: 'invoices.prefix', control: 'prefix', advanced: true },
          { key: 'invoices.billOfSupplyPrefix', control: 'prefix', advanced: true },
          { key: 'invoices.billPrefix', control: 'prefix', advanced: true },
          { key: 'invoices.creditNotePrefix', control: 'prefix', advanced: true },
        ],
      },
    ],
  },
  {
    id: 'cashAndShifts',
    sections: [
      { id: 'day', panel: 'daysAndShifts', settings: [{ key: 'shifts.dayCloseMode' }] },
      {
        id: 'drawers',
        settings: [
          { key: 'shifts.enabled' },
          { key: 'shifts.cashHandling', dependsOn: [on('shifts.enabled')] },
          { key: 'shifts.blindClose', dependsOn: [on('shifts.enabled')] },
          { key: 'shifts.varianceNoteAmount', dependsOn: [on('shifts.enabled')] },
          { key: 'shifts.varianceAlertAmount', dependsOn: [on('shifts.enabled')] },
          { key: 'hardware.drawerKickOnCash' },
        ],
      },
      { id: 'counters', panel: 'counters', settings: [] },
    ],
  },
  {
    id: 'foodAndOrders',
    sections: [
      { id: 'kitchen', settings: [{ key: 'fnb.sendToKitchenBeforePayment' }] },
      { id: 'pcOrders', settings: [{ key: 'fnb.kioskOrdering' }] },
      // Stock (ADR-0040, spec/13 §6.4): off by default; auto sold-out does nothing while it is off.
      { id: 'stock', settings: [{ key: 'inventory.enabled' }, { key: 'fnb.autoUnavailableOnZeroStock', dependsOn: [on('inventory.enabled')] }] },
    ],
  },
  {
    id: 'playersAndSignIn',
    sections: [
      {
        id: 'accounts',
        settings: [
          { key: 'kiosk.allowSignUp' },
          { key: 'auth.qrRequireOnSite' },
          { key: 'sessions.guestsEnabled' },
          { key: 'sessions.guestTicketValidityHours', display: hours, dependsOn: [on('sessions.guestsEnabled')] },
        ],
      },
      {
        id: 'codes',
        settings: [
          // HQ's platform switch (shown on HQ's screen only): off since the owner's instruction of 2026-10-08.
          { key: 'auth.phoneSignIn' },
          { key: 'auth.otpChannel' },
          { key: 'notifications.otpChannelOrder', control: 'channelOrder', dependsOn: [{ key: 'auth.otpChannel', when: { is: 'equals', value: 'both' } }] },
        ],
      },
      {
        // Moving a café's players from another system (F-HQ-40, ADR-0048): HQ's alone (shown on HQ's screens only).
        id: 'migration',
        settings: [
          { key: 'migration.claimWindowDays', display: days },
          { key: 'migration.carriedOverTimeValidityDays', display: days },
          { key: 'migration.reviewAmount' },
          { key: 'migration.reviewSeconds', display: { kind: 'duration', stored: 'seconds', shown: 'hours' } },
        ],
      },
      {
        // HQ's sales outreach (F-HQ-39, spec/14 §16): HQ's alone (shown on HQ's screens only).
        id: 'sales',
        settings: [{ key: 'leads.dailyOutreachCap', advanced: true }, { key: 'leads.followUpDays', display: days, advanced: true }],
      },
      {
        id: 'age',
        panel: 'ageRules',
        settings: [
          { key: 'players.minAge' },
          { key: 'players.ageRequiresVerifiedDob' },
          { key: 'compliance.idCheckRequired' },
          { key: 'compliance.acceptedIdMethods', dependsOn: [{ key: 'compliance.idCheckRequired', when: { is: 'not', value: 'never' } }] },
          {
            key: 'compliance.idCheckValidityMonths',
            display: { kind: 'duration', stored: 'months' },
            dependsOn: [{ key: 'compliance.idCheckRequired', when: { is: 'equals', value: 'always_verified' } }],
          },
        ],
      },
    ],
  },
  {
    id: 'staffLimits',
    sections: [
      {
        id: 'limits',
        panel: 'limits',
        settings: [
          { key: 'pos.manualDiscountMaxBps', display: { kind: 'percentBps' } },
          { key: 'pos.refundApprovalAmount' },
          { key: 'wallet.adjustMaxAmount' },
          { key: 'migration.counterClaimApprovalAmount' },
          { key: 'sessions.compMaxMinutesPerAction', display: minutes },
          { key: 'sessions.compMaxMinutesPerShift', display: minutes },
        ],
      },
      {
        id: 'security',
        settings: [
          { key: 'staff.require2faRoles', control: 'rolePicker' },
          { key: 'staff.pinSessionMinutes', display: minutes, advanced: true },
        ],
      },
    ],
  },
  {
    id: 'hardware',
    sections: [
      { id: 'printers', panel: 'printers', settings: [] },
      {
        id: 'power',
        settings: [
          { key: 'venue.powerSchedule' },
          { key: 'station.idlePowerAction' },
          { key: 'station.idlePowerMinutes', display: minutes, dependsOn: [{ key: 'station.idlePowerAction', when: { is: 'not', value: 'none' } }] },
        ],
      },
      {
        id: 'alerts',
        settings: [
          { key: 'station.alertGpuTempC', display: { kind: 'celsius' } },
          { key: 'station.alertExpiryHours', display: hours, advanced: true },
        ],
      },
      {
        id: 'software',
        settings: [
          // Set up PCs before play (ADR-0055, 2026-10-11): off by default; its time limit nests under it.
          { key: 'stations.setupBeforePlay' },
          { key: 'stations.setupMaxMinutes', display: minutes, dependsOn: [on('stations.setupBeforePlay')] },
          { key: 'station.clientChannel', advanced: true },
          { key: 'station.clientPinnedVersion', control: 'version', advanced: true, dependsOn: [{ key: 'station.clientChannel', when: { is: 'equals', value: 'pinned' } }] },
          { key: 'station.clientUpdateWindow', control: 'updateWindow', advanced: true },
          { key: 'stations.autoRelinkKnownHardware', advanced: true },
          // Diskless PCs (CCBoot, 2026-10-07, spec/10 §14): detected by default; a venue or class may say on or off.
          { key: 'station.disklessMode', advanced: true },
          { key: 'locker.maintenanceMinutes', display: minutes, advanced: true },
        ],
      },
    ],
  },
  {
    id: 'security',
    sections: [
      {
        id: 'apps',
        settings: [
          { key: 'locker.executionPolicy' },
          { key: 'locker.extraBlockedImages', control: 'fileNames' },
          { key: 'locker.blockAltF4InSession' },
          { key: 'locker.allowExplorer', advanced: true },
        ],
      },
      {
        id: 'devices',
        settings: [{ key: 'locker.usbStorage' }, { key: 'locker.extensionAllowlist', control: 'extensionIds', advanced: true }],
      },
      { id: 'windows', settings: [{ key: 'locker.allowedSettingsPages', control: 'settingsPages' }] },
    ],
  },
  {
    id: 'notifications',
    sections: [
      {
        id: 'receipts',
        settings: [{ key: 'notifications.receiptDefault' }, { key: 'receipts.autoEmail', under: 'notifications.receiptDefault' }],
      },
      { id: 'staffAlerts', settings: [{ key: 'notifications.floorToOnDutyOnly' }] },
      {
        id: 'players',
        settings: [
          { key: 'notifications.marketingMaxPerWeek', display: { kind: 'count', per: 'week' } },
          { key: 'notifications.quietHoursDefault', advanced: true },
        ],
      },
    ],
  },
  {
    id: 'privacyAndRecords',
    sections: [
      { id: 'privacy', settings: [{ key: 'support.notifyPlayerOnView' }] },
      {
        id: 'records',
        settings: [
          { key: 'compliance.idPhotoRetentionDays', display: days },
          { key: 'audit.retentionYears', display: { kind: 'duration', stored: 'years' } },
        ],
      },
    ],
  },
  {
    // Clavian Boot (spec/25 §14): only an organisation with the add-on is sent these, so the group appears with it.
    id: 'clavianBoot',
    sections: [
      { id: 'bootNetwork', settings: [{ key: 'boot.dhcpMode' }, { key: 'boot.autoAddPcs' }, { key: 'boot.menu' }] },
      { id: 'bootWriteback', settings: [{ key: 'boot.writebackPolicy' }, { key: 'boot.writebackQuotaGb' }] },
      {
        id: 'bootCache',
        settings: [{ key: 'boot.ramCacheGb' }, { key: 'boot.shaderCache' }, { key: 'boot.shaderCacheGb', dependsOn: [on('boot.shaderCache')] }],
      },
      { id: 'bootRollout', settings: [{ key: 'boot.rolloutRestart' }] },
      { id: 'bootServerUpdates', settings: [{ key: 'boot.serverUpdateChannel', advanced: true }] },
    ],
  },
];

/** A setting the map does not know goes to the job group its registry group belongs to, at its end. */
export const FALLBACK_GROUP: Readonly<Record<SettingGroup, JobGroupId>> = {
  venueProfile: 'venue',
  kiosk: 'sessionsAndPcs',
  sessions: 'timeAndPricing',
  offlinePlay: 'sessionsAndPcs',
  stationsAndLocker: 'security',
  pricing: 'timeAndPricing',
  payments: 'money',
  daysAndShifts: 'cashAndShifts',
  counters: 'cashAndShifts',
  taxAndInvoices: 'money',
  foodAndDrinks: 'foodAndOrders',
  reservations: 'sessionsAndPcs',
  loyalty: 'money',
  players: 'playersAndSignIn',
  printersAndHardware: 'hardware',
  notifications: 'notifications',
  integrations: 'notifications',
  staff: 'staffLimits',
  dataRetention: 'privacyAndRecords',
  featureFlags: 'privacyAndRecords',
  boot: 'clavianBoot',
};

/**
 * Old links name a registry group (`…/settings/payments#wallet.minTopupAmount`, the palette's past results, other
 * screens' links): the job group it now belongs to — or, with a key, the group that key is placed in.
 */
export const LEGACY_GROUP: Readonly<Record<string, JobGroupId>> = {
  ...FALLBACK_GROUP,
  venueProfile: 'venue',
  kiosk: 'sessionsAndPcs',
  sessions: 'timeAndPricing',
  stationsAndLocker: 'security',
  payments: 'money',
  printersAndHardware: 'hardware',
  staff: 'staffLimits',
};

export interface PlacedSetting extends Placement {
  readonly group: JobGroupId;
  readonly section: string;
  /** Its order on the whole screen. */
  readonly order: number;
}

const PLACED: ReadonlyMap<string, PlacedSetting> = (() => {
  const placed = new Map<string, PlacedSetting>();
  let order = 0;
  for (const group of LAYOUT) {
    for (const section of group.sections) {
      for (const setting of section.settings) placed.set(setting.key, { ...setting, group: group.id, section: section.id, order: order++ });
    }
  }
  return placed;
})();

/** Every key the map places, in screen order (the completeness test reads it). */
export const PLACED_KEYS: readonly string[] = LAYOUT.flatMap((group) => group.sections.flatMap((section) => section.settings.map((setting) => setting.key)));

/** Where a setting sits; a key the map does not know, after everything in its registry group's job group. */
export function placementOf(key: string, registryGroup?: string): PlacedSetting {
  const known = PLACED.get(key);
  if (known !== undefined) return known;
  const group = (registryGroup !== undefined ? FALLBACK_GROUP[registryGroup as SettingGroup] : undefined) ?? 'notifications';
  return { key, group, section: 'other', order: Number.MAX_SAFE_INTEGER };
}

export function layoutGroup(id: string): LayoutGroup | undefined {
  return LAYOUT.find((group) => group.id === id);
}

export function isJobGroup(id: string): id is JobGroupId {
  return (JOB_GROUP_IDS as readonly string[]).includes(id);
}

/** The job group a URL's group names: a job group as it is, an old registry group mapped — by the key when one is given. */
export function jobGroupOf(group: string, key?: string): JobGroupId | undefined {
  if (isJobGroup(group)) return group;
  if (key !== undefined && PLACED.has(key)) return PLACED.get(key)?.group;
  return LEGACY_GROUP[group];
}

/** Does `value` satisfy the condition? An unknown value (the master is not readable here) satisfies nothing. */
export function holds(condition: Condition, value: unknown): boolean {
  switch (condition.is) {
    case 'on':
      return value === true;
    case 'positive':
      return typeof value === 'number' && value > 0;
    case 'equals':
      return value === condition.value;
    case 'not':
      return value !== undefined && value !== condition.value;
  }
}

/** The masters a setting's dependencies name that do not hold, in order. */
export function unmetDependencies(placement: Placement, valueOf: (key: string) => unknown): Dependency[] {
  return (placement.dependsOn ?? []).filter((dependency) => {
    const value = valueOf(dependency.key);
    return value !== undefined && !holds(dependency.when, value);
  });
}
